import type { ProspectResponse } from '@common/types/prospects';
import { getMatterTypeLabel } from '@common/util';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material';
import { useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import {
    useProspectIntakeForm,
    useVerificationRequests,
    useVerificationStatuses,
    type VerificationStatusDetail,
} from '@/queries/prospectDetails';
import { deleteProspects } from '@/services/ProspectService';
import { convertUTCDateToEST } from '@/utils';
import { successSnackbar } from '@/utils/snackbar';
import SendIDVerificationModal from './SendIDVerificationModal';
import SendManualQuoteModal from './SendManualQuoteModal/SendManualQuoteModal';

interface HeaderTab {
    label: string;
    route: string;
}

const ALL_PROSPECT_TABS: HeaderTab[] = [
    {
        label: 'Summary',
        route: 'summary',
    },
    {
        label: 'Quote form response',
        route: 'quote-from-response',
    },
    {
        label: 'Intake form response',
        route: 'intake-from-response',
    },
    {
        label: 'Email timeline',
        route: 'email-timeline',
    },
    {
        label: 'Client documents',
        route: 'client-documents',
    },
    {
        label: 'ID Verification',
        route: 'id-verification',
    },
    {
        label: 'Client Invoice',
        route: 'client-invoice',
    },
] as const;

const INTAKE_ONLY_PROSPECT_TABS: HeaderTab[] = [
    ALL_PROSPECT_TABS[0],
    // Omit quote form response
    ALL_PROSPECT_TABS[2],
    // Omit email timeline
    ALL_PROSPECT_TABS[4],
    ALL_PROSPECT_TABS[5],
    ALL_PROSPECT_TABS[6],
];

const INTAKE_ONLY_PROSPECT_TABS_MANUALLY: HeaderTab[] = [
    ALL_PROSPECT_TABS[0],
    // Omit quote form response
    ALL_PROSPECT_TABS[2],
    ALL_PROSPECT_TABS[3],
    ALL_PROSPECT_TABS[4],
    ALL_PROSPECT_TABS[5],
    ALL_PROSPECT_TABS[6],
];

const VERSION_DATE_FMT = 'yyyy/M/d h:mm a';

interface Props {
    prospectId?: string;
    prospectTabs?: string;
    prospect: ProspectResponse;
    selectedVersionId: number | null;
    onSelectedVersionIdChange: (versionId: number | null) => void;
    isVersionFetching?: boolean;
}

export default function ProspectHeader({
    prospectId,
    prospectTabs,
    prospect,
    selectedVersionId,
    onSelectedVersionIdChange,
    isVersionFetching = false,
}: Props) {
    const { data: intakeForm } = useProspectIntakeForm(prospectId);
    const intakeFormStatus = intakeForm?.status ?? null;
    const intakeFormData = intakeForm?.data;
    const hasSubmittedIntakeForm = intakeFormStatus === 200 && !intakeFormData?.isDraft;
    const { data: verificationRequestResponseData = [] } = useVerificationRequests(
        prospectId,
        hasSubmittedIntakeForm,
    );
    const [isSendQuoteModalOpen, setIsSendQuoteModalOpen] = useState(false);
    const [isIDVerificationModalOpen, setIsIDVerificationModalOpen] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const theme = useTheme();
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const versionSelectLabelId = useId();

    const availableTabs =
        prospect?.templateType === 'IntakeOnly' && prospect?.createdAs === 'Manually'
            ? INTAKE_ONLY_PROSPECT_TABS_MANUALLY
            : prospect?.templateType === 'IntakeOnly'
              ? INTAKE_ONLY_PROSPECT_TABS
              : ALL_PROSPECT_TABS;

    const currentTab = useMemo(() => {
        const tabIdx = availableTabs.findIndex((tab) => tab.route === prospectTabs);
        return tabIdx === -1 ? 0 : tabIdx;
    }, [prospectTabs, availableTabs]);

    const handleTabChange = (tabIndex: number) => {
        navigate(`/prospects/${prospectId}/${availableTabs[tabIndex].route}`);
    };

    const handleSendIDVerifcation = () => {
        setIsIDVerificationModalOpen(true);
    };

    const { data: latestStatuses = {}, isFetching: isVerificationStatusLoading } =
        useVerificationStatuses(
            prospectId,
            verificationRequestResponseData,
            hasSubmittedIntakeForm,
        );

    const intakeContactDetails = useMemo(() => {
        const userContactDetails = intakeFormData?.userContactDetails ?? [];
        const details =
            userContactDetails.length > 0
                ? userContactDetails.map((contact) => ({
                      contactDetailId: contact.contactDetailId,
                  }))
                : (intakeFormData?.contactResponses ?? []).map((contact) => ({
                      contactDetailId: contact.contactId,
                  }));

        return {
            details,
            prospectVersionId: intakeFormData?.prospectVersion?.prospectVersionId ?? null,
        };
    }, [intakeFormData]);

    function areAllEmailsInStatus(
        latestStatuses: Record<string, VerificationStatusDetail>,
        intakeContactDetails: { details: { contactDetailId: string }[] },
    ): boolean {
        const details = intakeContactDetails?.details ?? [];
        if (details.length === 0) return false;

        const statusEmails = Object.values(latestStatuses)
            .filter((entry) => entry.requesterClientRequestId)
            .map((entry) => entry.requesterClientRequestId);

        return details.every((data) => statusEmails.includes(data.contactDetailId));
    }

    const showButtonEnabled = areAllEmailsInStatus(latestStatuses, intakeContactDetails);

    const convertedEST = convertUTCDateToEST(prospect.createdAt, VERSION_DATE_FMT);

    const confirmDeleteProspect = async () => {
        if (prospect.prospectId) {
            setIsDeleteLoading(true);
            try {
                await deleteProspects([prospect.prospectId]);
                setIsDeleteLoading(false);
                setOpenDeleteDialog(false);
                successSnackbar('Prospect deleted successfully');
                navigate('/prospects');
            } catch (error) {
                console.error(error);
                setIsDeleteLoading(false);
            }
        }
    };

    const handleVersionChange = (versionId: number) => {
        onSelectedVersionIdChange(versionId);
    };

    return (
        <Box
            sx={{
                padding: '24px 24px 0px 24px',
                height: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#FFFFFF',
            }}
        >
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" sx={{ color: 'neutral.gray' }} />}
            >
                <Link
                    underline="none"
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'neutral.gray',
                        cursor: 'pointer',
                    }}
                    onClick={() => navigate('/prospects')}
                >
                    Prospects
                </Link>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                    }}
                >
                    {prospect.prospectName}
                </Typography>
            </Breadcrumbs>
            <Typography sx={{ marginBottom: '8px' }} variant="h1">
                {prospect.prospectName}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                >
                    <Chip
                        sx={{
                            borderRadius: '5px',
                            backgroundColor: theme.palette.sidenav.main,
                            color: '#FFFFFF',
                        }}
                        label={getMatterTypeLabel(prospect.matterType)}
                    />
                    {/* Future: "Archived" badge will go here */}
                    <Typography>
                        Created on:{' '}
                        <span style={{ fontWeight: 500, color: theme.palette.sidenav.main }}>
                            {convertedEST}
                        </span>{' '}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                >
                    {hasSubmittedIntakeForm &&
                        prospectTabs !== 'quote-from-response' &&
                        prospectTabs !== 'email-timeline' && (
                            <FormControl size="small" disabled={isVersionFetching}>
                                <InputLabel id={versionSelectLabelId}>Intake version</InputLabel>
                                <Select
                                    labelId={versionSelectLabelId}
                                    label="Intake version"
                                    value={selectedVersionId ?? ''}
                                    sx={{ height: 36 }}
                                    onChange={(e) => handleVersionChange(Number(e.target.value))}
                                >
                                    {(prospect?.prospectVersions ?? []).map((option, index) => (
                                        <MenuItem
                                            key={option.prospectVersionId}
                                            value={option.prospectVersionId}
                                        >
                                            {index === 0
                                                ? `Current - ${convertUTCDateToEST(option.createdAt, VERSION_DATE_FMT)}`
                                                : convertUTCDateToEST(
                                                      option.createdAt,
                                                      VERSION_DATE_FMT,
                                                  )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    {prospect?.templateType !== 'IntakeOnly' &&
                        prospectTabs !== 'id-verification' && (
                            <Button
                                disabled={prospect.clientAcceptanceStatus === 'Accepted'}
                                aria-label="send quote"
                                variant={'contained'}
                                startIcon={<EmailOutlinedIcon />}
                                onClick={() => setIsSendQuoteModalOpen(true)}
                            >
                                Send quote
                            </Button>
                        )}
                    {hasSubmittedIntakeForm && !isVerificationStatusLoading && (
                        <Button
                            sx={{
                                height: 36,
                                fontSize: '14px',
                                padding: '0 16px',
                                whiteSpace: 'nowrap',
                                minWidth: '140px',
                            }}
                            variant="contained"
                            onClick={handleSendIDVerifcation}
                            disabled={isVerificationStatusLoading || showButtonEnabled}
                        >
                            Send ID Verification
                        </Button>
                    )}
                    <Button
                        aria-label="archive"
                        variant={'outlined'}
                        onClick={() => setOpenDeleteDialog(true)}
                        startIcon={<ArchiveOutlinedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                fontSize: 16,
                            },
                        }}
                    >
                        Archive
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: theme.palette.neutral.N_100,
                    '& .MuiTabs-flexContainer': {
                        gap: '24px',
                    },
                    '& .MuiButtonBase-root': {
                        color: theme.palette.neutral.gray,
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '20px',
                        paddingX: '0px',
                        width: 'fit-content',
                        minWidth: '0px',
                    },
                    '& .Mui-selected': {
                        color: theme.palette.primary.main,
                        fontWeight: '600',
                    },
                }}
            >
                <Tabs
                    value={currentTab}
                    variant="standard"
                    onChange={(_event, value: number) => handleTabChange(value)}
                >
                    {availableTabs.map((tab) => (
                        <Tab key={tab.route} label={tab.label} sx={{ textTransform: 'none' }} />
                    ))}
                </Tabs>
            </Box>
            {openDeleteDialog && (
                <AlertDialog
                    open
                    isLoading={isDeleteLoading}
                    handleClose={() => setOpenDeleteDialog(false)}
                    title="Are you sure you want to delete the prospect?"
                    message="By clicking Accept you will delete the prospect. To recover this prospect, you will need to contact Lexo Support."
                    handleConfirm={confirmDeleteProspect}
                />
            )}
            {isSendQuoteModalOpen && (
                <SendManualQuoteModal
                    prospectId={prospectId}
                    prospect={prospect}
                    onClose={() => setIsSendQuoteModalOpen(false)}
                />
            )}
            {isIDVerificationModalOpen && (
                <SendIDVerificationModal
                    prospectId={prospectId}
                    prospect={prospect}
                    onClose={() => setIsIDVerificationModalOpen(false)}
                />
            )}
        </Box>
    );
}
